import { useEffect, useRef, type FunctionComponent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { media } from '@atlaskit/primitives/compiled';
import { TrelloIcon } from '@atlaskit/logo';
import Tile from '@atlaskit/tile';

import McpPermissions from './McpPermissions/McpPermissions';
import AgentMcpCapabilitiesWarning from './AgentMcpCapabilitiesWarning/AgentMcpCapabilitiesWarning';
import SiteSelector from './SiteSelector/SiteSelector';
import BitbucketWorkspaceSelector from './BitbucketWorkspaceSelector/BitbucketWorkspaceSelector';
import TrelloWorkspaceSelector from './TrelloWorkspaceSelector/TrelloWorkspaceSelector';
import Headline from '../shared/agent-consent/Headline';
import McpSteps from './McpSteps/McpSteps';
import McpInfoCard from './McpInfoCard/McpInfoCard';
import McpDetails from './McpDetails/McpDetails';
import McpBanners from './McpBanners/McpBanners';
import McpPolicyAlerts from './McpPolicyAlerts/McpPolicyAlerts';
import SiteBypassBanner from '../shared/agent-consent/SiteBypassBanner/SiteBypassBanner';
import { useMcpContext } from './McpContextProvider';
import { useMcpBranding } from './mcpTerminology';
import { useInfo } from '../../Flow/ConsentContextProvider';
import {
  getForgeMcpAppIcon,
  getForgeMcpAppName,
} from '#definitions/consent-info';
import type { AgentMcpDisplayMetadata } from './domain/useAgentMcpMetadata';

const forgeIconImageStyles = xcss({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
});

const rootStyles = xcss({
  width: '100%',
  maxWidth: '100%',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  [media.above.md]: {
    maxWidth: '1280px',
  },
});

const panelsRowStyles = xcss({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  flex: '1 1 auto',
  minHeight: '0',
  width: '100%',
  flexWrap: 'wrap',
  [media.above.sm]: {
    minHeight: 'calc(100vh - 56px)',
  },
});

const leftPanelStyles = xcss({
  boxSizing: 'border-box',
  display: 'flex',
  width: '100%',
  minWidth: '100%',
  flexShrink: 0,
  paddingInline: 'space.200',
  paddingBlockStart: 'space.250',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'space.200',
  position: 'static',
  top: 'space.0',
  alignSelf: 'flex-start',
  overflowY: 'visible',
  [media.above.sm]: {
    padding: 'space.0',
    paddingBlockStart: 'space.400',
    marginInline: 'space.300',
    marginBlockStart: 'space.400',
    marginBlockEnd: 'space.800',
    width: '300px',
    minWidth: 'min(100%, 300px)',
    position: 'sticky',
    maxHeight: '100vh',
    overflowY: 'auto',
  },
  [media.above.md]: {
    marginInline: 'space.600',
    width: '350px',
    minWidth: 'min(100%, 350px)',
  },
  [media.above.lg]: {
    marginInline: 'space.800',
  },
});

// Supporting content for the left sidebar: on mobile the panel stacks above
// the consent content, where it would only push the selectors down.
const hideOnMobileStyles = xcss({
  display: 'none',
  [media.above.sm]: { display: 'block', width: '100%' },
});

// Added to hideOnMobileStyles to pin the info card to the bottom of the
// sidebar: the auto margin takes up whatever room the stretched panel has left
// below the steps.
const infoCardPinnedStyles = xcss({
  [media.above.sm]: {
    marginBlockStart: 'auto',
    marginBlockEnd: 'space.600',
  },
});

// Stretches the sidebar to the row so the info card has room to sit at its
// bottom; still capped at the viewport by leftPanelStyles.
const leftPanelStretchStyles = xcss({
  [media.above.sm]: { alignSelf: 'stretch' },
});

// The headline is the only part of the left panel shown on mobile, so the review
// step drops the whole panel there rather than leave its padding behind.
const leftPanelHiddenOnMobileStyles = xcss({
  display: 'none',
  [media.above.sm]: { display: 'flex' },
});

const rightPanelStyles = xcss({
  height: 'auto',
  boxSizing: 'border-box',
  display: 'flex',
  paddingInline: 'space.200',
  paddingBlockStart: 'space.250',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'space.250',
  flex: '1 0 100%',
  width: '100%',
  minWidth: '100%',
  borderLeftWidth: 0,
  borderLeftStyle: 'solid',
  borderLeftColor: 'color.border',
  // @ts-expect-error — 500px is an intentional custom value not in design tokens
  paddingBlockEnd: '500px',
  [media.above.sm]: {
    paddingBlockStart: 'space.800',
    paddingInline: 'space.300',
    flex: '1 1 0%',
    minWidth: '0',
    borderLeftWidth: 'border.width',
    // @ts-expect-error — 120px is an intentional custom value not in design tokens
    paddingBlockEnd: '120px',
  },
  [media.above.md]: {
    paddingInline: 'space.600',
  },
  [media.above.lg]: {
    paddingInline: 'space.800',
  },
});

/**
 * The steps differ in height, so changing step from partway down the page would
 * land mid-content. Brings the top of the ref'd element into view when `key`
 * changes (not on first render). Uses scrollIntoView rather than scrolling
 * `window` so whichever ancestor actually scrolls is the one that moves.
 */
function useScrollIntoViewOnChange<T extends HTMLElement>(key: unknown) {
  const ref = useRef<T>(null);
  const previousKey = useRef(key);
  useEffect(() => {
    if (previousKey.current === key) {
      return;
    }
    previousKey.current = key;
    ref.current?.scrollIntoView?.({ block: 'start' });
  }, [key]);
  return ref;
}

type MCPProps = {
  /** Loaded agent metadata; present only for Agent MCP consent. */
  agentMetadata?: AgentMcpDisplayMetadata;
};

const MCP: FunctionComponent<MCPProps> = ({ agentMetadata }) => {
  const { forgeMcpConsentTarget } = useInfo();
  const {
    scopeClassification,
    trelloRequestedButNoAccess,
    availableTrelloWorkspaces,
    hasTrelloInboxPlannerScopes,
    showSiteSelector,
    showBitbucketSelector,
    isTwoStepConsentEnabled,
    // With the two-step consent on, the user picks targets first, then reviews
    // what they grant. Each step shows only its own half.
    isSelectStep,
    isReviewStep,
    consentStep,
  } = useMcpContext();

  const rootRef = useScrollIntoViewOnChange<HTMLDivElement>(consentStep);
  // Outlives SiteSelector, which the review step unmounts.
  const prefilledSiteUrlRef = useRef<string | null>(null);
  const { product } = useMcpBranding();
  const showTrelloWorkspaceSelector =
    scopeClassification.hasTrello || trelloRequestedButNoAccess;
  const isTrelloOnly =
    showTrelloWorkspaceSelector && !scopeClassification.hasAtlassian;
  // Signed in to an account with no Trello workspaces and nothing else to
  // grant: the panel is a dead end, so the app details are noise.
  const isTrelloOnlyDeadEnd =
    isTrelloOnly &&
    availableTrelloWorkspaces.length === 0 &&
    !hasTrelloInboxPlannerScopes;
  const trelloOnlyIcon = isTrelloOnly ? (
    <TrelloIcon size="xlarge" appearance="brand" />
  ) : undefined;
  const forgeAppName = getForgeMcpAppName({ forgeMcpConsentTarget });
  const forgeAppIcon = getForgeMcpAppIcon({ forgeMcpConsentTarget });
  const agentIcon = agentMetadata?.icon ? (
    <Tile label={agentMetadata.name} size="xlarge" isInset={false}>
      <Box
        as="img"
        src={agentMetadata.icon}
        alt=""
        xcss={forgeIconImageStyles}
      />
    </Tile>
  ) : undefined;
  const targetIcon = agentMetadata ? (
    agentIcon
  ) : forgeAppName ? (
    <Tile label={forgeAppName} size="xlarge" isInset={false}>
      {forgeAppIcon ? (
        <Box as="img" src={forgeAppIcon} alt="" xcss={forgeIconImageStyles} />
      ) : undefined}
    </Tile>
  ) : (
    trelloOnlyIcon
  );

  return (
    <Box ref={rootRef} xcss={rootStyles}>
      <Box xcss={panelsRowStyles}>
        <Box
          xcss={[
            leftPanelStyles,
            isTwoStepConsentEnabled && leftPanelStretchStyles,
            isReviewStep && leftPanelHiddenOnMobileStyles,
          ]}
        >
          <Headline
            icon={targetIcon}
            product={product}
            agentName={agentMetadata?.name}
            agentDescription={agentMetadata?.description}
          />
          {isTwoStepConsentEnabled ? (
            <>
              <Box xcss={hideOnMobileStyles}>
                <McpSteps />
              </Box>
              <Box xcss={[hideOnMobileStyles, infoCardPinnedStyles]}>
                <McpInfoCard />
              </Box>
            </>
          ) : (
            <McpInfoCard />
          )}
        </Box>
        <Box xcss={[rightPanelStyles]}>
          <McpBanners />
          {!isReviewStep &&
            !trelloRequestedButNoAccess &&
            !isTrelloOnlyDeadEnd && <McpDetails />}
          {agentMetadata ? (
            <AgentMcpCapabilitiesWarning agentName={agentMetadata.name} />
          ) : (
            <>
              {!isReviewStep && (
                <>
                  <SiteBypassBanner />
                  {showSiteSelector && (
                    <SiteSelector prefilledSiteUrlRef={prefilledSiteUrlRef} />
                  )}
                  {showBitbucketSelector && <BitbucketWorkspaceSelector />}
                </>
              )}
              {showTrelloWorkspaceSelector && (
                <TrelloWorkspaceSelector showHeading={isTrelloOnly} />
              )}
              <McpPolicyAlerts />
              {!isSelectStep && <McpPermissions />}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default MCP;
